import { useCallback, useState } from 'react';
import { IconUpload } from '@picsart/design-system/foundation/icons/IconUpload';
import { useTranslator } from '@picsart/rc/services/localization';
import UploadSourceMenu from '@/components/UploadSourceMenu';
import sendUploadClickAnalytics from '@/utils/analytics/sendUploadClickAnalytics';
import useStyles from './styles';
import {
  getInvalidTitle,
  getInvalidHint,
} from '@/utils/upload/getUploadErrorMessages';
import type { DropZoneProps } from './types';

const DropZone = ({
  accept,
  title,
  allowedExtensions,
  hasError,
  hasUploadError,
  showInvalidState,
  isDragging,
  onSelectFromDrive,
  onSelectFromCanvas,
  onSelectFromNativePicker,
  hasCanvasImages,
  onOpenFilePicker,
  onClearInvalidFeedback,
  onDrop,
  onDragOver,
  onDragEnter,
  onDragLeave,
}: DropZoneProps) => {
  const classes = useStyles({ hasError: hasError || hasUploadError });
  const translate = useTranslator();
  const [showSourceMenu, setShowSourceMenu] = useState(false);

  const hasSourceMenu = !!onSelectFromDrive || !!hasCanvasImages;

  const handleClick = useCallback(() => {
    // Clear invalid state when user clicks to try again
    onClearInvalidFeedback();
    if (onSelectFromNativePicker) {
      sendUploadClickAnalytics();
      onSelectFromNativePicker();
      return;
    }
    if (hasSourceMenu) {
      setShowSourceMenu(true);
    } else {
      sendUploadClickAnalytics();
      onOpenFilePicker();
    }
  }, [
    onSelectFromNativePicker,
    hasSourceMenu,
    onOpenFilePicker,
    onClearInvalidFeedback,
  ]);

  const handleSelectFromDevice = useCallback(() => {
    sendUploadClickAnalytics();
    onOpenFilePicker();
  }, [onOpenFilePicker]);

  const handleSelectFromDrive = useCallback(() => {
    sendUploadClickAnalytics();
    onSelectFromDrive?.();
  }, [onSelectFromDrive]);

  const invalidTitle = getInvalidTitle({ hasUploadError, accept });
  const invalidHintText = getInvalidHint({ hasUploadError, allowedExtensions });

  const dropzoneElement = (
    <button
      type="button"
      className={`${classes.dropzone} ${
        showInvalidState
          ? classes.dropzoneInvalid
          : isDragging
          ? classes.dropzoneDragging
          : ''
      }`}
      onClick={handleClick}
      onDrop={(e) => {
        sendUploadClickAnalytics();
        onDrop(e);
      }}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      data-testid="upload-dropzone"
    >
      <div className={classes.wrapper}>
        <div className={classes.iconWrapper}>
          <IconUpload fill="currentColor" className={classes.icon} />
        </div>
        <span className={classes.text}>
          {showInvalidState
            ? translate(invalidTitle)
            : translate(title || 'Add your image')}
        </span>
        <span className={classes.hint}>
          {showInvalidState
            ? translate(invalidHintText)
            : translate(
                hasSourceMenu
                  ? 'Upload from device, Drive, or Canvas'
                  : 'Drop file here or tap to upload'
              )}
        </span>
      </div>
    </button>
  );

  return (
    <div className={classes.dropzoneWrapper}>
      {hasSourceMenu ? (
        <UploadSourceMenu
          reference={dropzoneElement}
          isOpen={showSourceMenu}
          onClose={() => setShowSourceMenu(false)}
          onSelectFromDevice={handleSelectFromDevice}
          onSelectFromDrive={
            onSelectFromDrive ? handleSelectFromDrive : undefined
          }
          onSelectFromCanvas={onSelectFromCanvas}
          hasCanvasImages={hasCanvasImages}
        />
      ) : (
        dropzoneElement
      )}
    </div>
  );
};

export default DropZone;
